import React, {
  useRef,
  FC,
  ReactNode,
  MouseEvent,
  useState,
  useCallback,
  useEffect,
  ReactElement,
  Ref,
} from "react";

import { Placement } from "@popperjs/core";
import classnames from "classnames";

import { MenuContextProvider } from "./context";
import { StyledMenu } from "./Styles";
import useForkRef from "../../utils/useForkRef";
import { Overlay } from "../Overlay";
import { Popper } from "../Popper";
import { Window } from "../Window";

interface ElementWithRef<T> extends ReactElement {
  ref?: Ref<T>;
}

// source: https://twitter.com/mattpocockuk/status/1671908303918473217/photo/1
// eslint-disable-next-line @typescript-eslint/ban-types
export type MenuMode = "normal" | "wider" | "tiny" | "widest";

export interface IMenu {
  /** Menu content */
  children:
    | ReactNode
    | ((childProps: { forceClose: () => void }) => JSX.Element);
  /** On menu element click */
  onMenuClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Handle scroll bar */
  handleScroll?: boolean;
  /** Disable Focus Lock */
  disableFocusLock?: boolean;
  /** Menu width mode */
  mode?: MenuMode;
  /** Should menu be open when it's mounted */
  open?: boolean;
  /** Callback triggered when modal has been opened */
  onOpen?: () => void;
  /** Callback triggered when modal has been closed */
  onClose?: () => void;
  /** Before close callback */
  onBeforeClose?: () => boolean;
  /** Position Menu */
  position?: Placement;
  /** Target element */
  target?: ElementWithRef<Element>;
  className?: string;
  /** Menu classes */
  menuClassName?: string;
  /** Popper class name*/
  popperClassName?: string;
  /** Background style for clickable element after the menu is open (opacity, color, etc...) */
  backgroundElementClass?: string;
  /** Should Window be positioned absolutely */
  windowAbsolutelyPositioned?: boolean;
  /** Popper on open */
  onPopperOpen?: (...args) => void;
}

export const Menu: FC<IMenu> = ({
  children,
  disableFocusLock = false,
  onMenuClick,
  handleScroll = true,
  mode = "normal",
  open: defaultOpen = false,
  onOpen,
  onClose,
  onBeforeClose,
  position = "bottom-start",
  target,
  className,
  menuClassName,
  popperClassName,
  backgroundElementClass,
  windowAbsolutelyPositioned = false,
  onPopperOpen,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [childNode, setChildNode] = useState<Element | null>();
  const elementRef = useRef<HTMLElement | null>(null);
  const isOpenAtLeastOnce = useRef(false);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const handleRefRef = useForkRef(
    target ? target.ref : undefined,
    setChildNode
  );
  const handleRef = useForkRef(handleRefRef, elementRef);

  const handleOpen = useCallback(
    (
      event: Pick<MouseEvent<Element>, "preventDefault" | "stopPropagation">
    ) => {
      event?.preventDefault();
      event?.stopPropagation();
      setOpen(true);
      typeof onOpen === "function" && onOpen();
      typeof target?.props?.onClick === "function" &&
        target.props.onClick(event);
    },
    [onOpen, target?.props]
  );

  const handleClose = useCallback(() => {
    if (typeof onBeforeClose === "function" && !onBeforeClose()) {
      return;
    }
    setOpen(false);
    typeof onClose === "function" && onClose();
  }, [onClose, onBeforeClose]);

  const childProps = {
    forceClose: handleClose,
  };

  const targetProps = {
    open,
    ref: handleRef,
    onClick: handleOpen,
  };

  useEffect(() => {
    if (open) {
      isOpenAtLeastOnce.current = true;
    } else if (!open && isOpenAtLeastOnce.current) {
      elementRef.current?.focus();
      isOpenAtLeastOnce.current = false;
    }
  }, [open]);

  return (
    <>
      {target && React.cloneElement(target, targetProps)}
      {open ? (
        <Window
          onClose={handleClose}
          disableScrollLock={!handleScroll}
          disableFocusLock={disableFocusLock}
          style={{
            position: windowAbsolutelyPositioned ? "absolute" : "fixed",
          }}
        >
          <Overlay
            className={backgroundElementClass}
            onClick={handleClose}
            tabIndex={-1}
            disableBackgroundColor
          />
          <Popper
            anchorEl={childNode}
            open={childNode ? open : false}
            placement={position}
            className={popperClassName}
            tabIndex={-1}
            afterWrite={onPopperOpen}
          >
            <StyledMenu
              className={classnames(
                "c-simple-menu__paper",
                `c-simple-menu__${mode}`,
                menuClassName,
                className
              )}
              $mode={mode}
              onMouseDown={onMenuClick}
            >
              <MenuContextProvider value={{ open, setOpen }}>
                {typeof children === "function"
                  ? children(childProps)
                  : children}
              </MenuContextProvider>
            </StyledMenu>
          </Popper>
        </Window>
      ) : null}
    </>
  );
};

Menu.displayName = "Menu";
